import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Spinner, Empty, StatusBadge } from "@/components/common";
import { useApi, errMsg, fmtDate } from "@/lib/api";
import { useMe } from "@/context/MeContext";
import { USER_STATUS, ROLE_LABEL, PERM_LABEL } from "@/lib/labels";

const FILTERS = { all: "الكل", pending: "بانتظار الاعتماد", approved: "معتمد", rejected: "مرفوض", new: "لم يكمل التسجيل" };

const OwnerRow = ({ u, projects, act }) => (
  <tr className="border-t border-[#F0EADB]" data-testid={`owner-row-${u.user_id}`}>
    <td className="p-4"><p className="font-semibold">{u.name || "—"}</p><p className="text-xs text-[#5C7067]">{u.email}</p></td>
    <td className="p-4 ltr-num">{u.phone}</td>
    <td className="p-4">{projects[u.project_id] || "—"}</td>
    <td className="p-4">{u.unit}</td>
    <td className="p-4"><StatusBadge map={USER_STATUS} status={u.status} testId={`owner-status-${u.user_id}`} /></td>
    <td className="p-4">
      <div className="flex justify-end gap-1">
        {u.status !== "approved" && u.project_id && <Button size="sm" className="rounded-full bg-emerald-700 hover:bg-emerald-800" onClick={() => act(u, { status: "approved" })} data-testid={`approve-owner-${u.user_id}`}><Check className="ml-1 h-4 w-4" />اعتماد</Button>}
        {u.status !== "rejected" && u.status !== "new" && <Button size="sm" variant="outline" className="rounded-full border-red-200 text-red-600" onClick={() => {
          const reason = window.prompt("سبب الرفض / الإيقاف (اختياري)") ?? null;
          if (reason !== null) act(u, { status: "rejected", reject_reason: reason });
        }} data-testid={`reject-owner-${u.user_id}`}><X className="ml-1 h-4 w-4" />{u.status === "approved" ? "إيقاف" : "رفض"}</Button>}
      </div>
    </td>
  </tr>
);

const StaffRow = ({ u, roles, perms, isSelf, setRole, act }) => (
  <tr className="border-t border-[#F0EADB]" data-testid={`staff-row-${u.user_id}`}>
    <td className="p-4"><p className="font-semibold">{u.name || "—"}</p><p className="text-xs text-[#5C7067]">{u.email}</p></td>
    <td className="p-4">
      <Select dir="rtl" value={u.role} disabled={isSelf} onValueChange={(r) => setRole(u, r)}>
        <SelectTrigger className="w-36" data-testid={`role-select-${u.user_id}`}><SelectValue /></SelectTrigger>
        <SelectContent>{roles.map((r) => <SelectItem key={r} value={r}>{ROLE_LABEL[r]}</SelectItem>)}</SelectContent>
      </Select>
    </td>
    <td className="p-4">
      {u.role === "employee" ? (
        <div className="flex flex-wrap gap-3">
          {perms.map((p) => (
            <label key={p} className="flex items-center gap-1.5 text-sm">
              <Checkbox checked={(u.permissions || []).includes(p)} data-testid={`perm-${p}-${u.user_id}`}
                onCheckedChange={(c) => act(u, { permissions: c ? [...(u.permissions || []), p] : (u.permissions || []).filter((x) => x !== p) })} />
              {PERM_LABEL[p]}
            </label>
          ))}
        </div>
      ) : <span className="text-xs text-[#5C7067]">{u.role === "admin" ? "صلاحيات كاملة" : "—"}</span>}
    </td>
    <td className="p-4 text-xs text-[#5C7067]">{fmtDate(u.created_at)}</td>
  </tr>
);

export default function AdminUsers({ mode }) {
  const api = useApi();
  const { me } = useMe();
  const [data, setData] = useState(null);
  const [projects, setProjects] = useState({});
  const [filter, setFilter] = useState(mode === "owners" ? "pending" : "all");
  const load = useCallback(() => {
    api("get", "/users").then(setData).catch((e) => toast.error(errMsg(e)));
    api("get", "/admin/projects").then((ps) => setProjects(Object.fromEntries(ps.map((p) => [p.id, p.name])))).catch(() => {});
  }, [api]);
  useEffect(() => {
    load();
  }, [load]);
  const act = async (u, body) => {
    try {
      await api("patch", `/admin/users/${u.user_id}`, body);
      toast.success("تم التحديث");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const setRole = async (u, role) => {
    try {
      await api("patch", `/users/${u.user_id}/role`, { role });
      toast.success("تم تغيير الدور");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  if (!data) return <Spinner />;

  if (mode === "owners") {
    const owners = data.users.filter((u) => u.role === "customer" && (filter === "all" || u.status === filter));
    return (
      <div data-testid="admin-owners">
        <div className="mb-4 flex gap-2 overflow-x-auto">
          {Object.entries(FILTERS).map(([k, l]) => (
            <button key={k} onClick={() => setFilter(k)} data-testid={`owners-filter-${k}`} className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-semibold ${filter === k ? "bg-[#0F382C] text-white" : "bg-white border border-[#E5DEC9]"}`}>{l}</button>
          ))}
        </div>
        {owners.length === 0 ? <Empty title="لا يوجد ملاك في هذه الفئة" testId="owners-empty" /> : (
          <div className="overflow-x-auto rounded-2xl border border-[#E5DEC9] bg-white">
            <table className="w-full min-w-[760px] text-sm">
              <thead className="bg-[#F7F4EF] text-[#5C7067]"><tr>{["المالك", "الجوال", "المشروع", "الوحدة", "الحالة", ""].map((h, i) => <th key={i} className="p-4 text-right font-semibold">{h}</th>)}</tr></thead>
              <tbody>{owners.map((u) => <OwnerRow key={u.user_id} u={u} projects={projects} act={act} />)}</tbody>
            </table>
          </div>
        )}
      </div>
    );
  }

  return (
    <div data-testid="admin-staff">
      <p className="mb-4 rounded-xl bg-[#F7F4EF] p-4 text-sm text-[#5C7067]">
        لإضافة موظف: يطلب منه إنشاء حساب في دارنا، ثم غيّر دوره هنا إلى "موظف" وحدد صلاحياته. الموظف يرى فقط المهام المسندة إليه.
      </p>
      <div className="overflow-x-auto rounded-2xl border border-[#E5DEC9] bg-white">
        <table className="w-full min-w-[720px] text-sm">
          <thead className="bg-[#F7F4EF] text-[#5C7067]"><tr>{["المستخدم", "الدور", "الصلاحيات", "تاريخ الإنشاء"].map((h) => <th key={h} className="p-4 text-right font-semibold">{h}</th>)}</tr></thead>
          <tbody>{data.users.map((u) => <StaffRow key={u.user_id} u={u} roles={data.roles} perms={data.permissions} isSelf={u.user_id === me.user_id} setRole={setRole} act={act} />)}</tbody>
        </table>
      </div>
    </div>
  );
}
